import React, { useCallback } from 'react';
import {
    Activity,
    FileEntry,
    useCourseUnitsDataStudentsDataQuery,
} from '@graphql/generated-graphql';
import { useFileEntry } from '@hooks/files/useFileEntry';
import { ActivityIconSVG } from '@components/common/svgs/ActivityIconSVG';
import { QuizActivitySVG } from '@components/common/svgs/QuizActivitySVG';
import { useCourseStateContext } from '@components/context/CourseContext';
import { useQueryParams } from '@hooks/useQueryParams';
import { QuizCompletedSVG } from '@components/common/svgs/QuizCompletedSVG';
import { AssignmentCompletedSVG } from '@components/common/svgs/AssignmentCompletedSVG';
import { isActivityCompleted } from '@utils/activity';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { UnitActivitiesMenuList } from './UnitsComponents';

export function ActivityMenuListStudentView({ unitId }: { unitId: string }) {
    const { id } = useQueryParams(['id']);
    const [user] = useUserSession();
    const { data: course } = useCourseStateContext();
    const { data, isLoading } = useCourseUnitsDataStudentsDataQuery(getClient('courses'), {
        ids: [id],
        unitIds: [unitId],
        userId: user?.id,
        includeActivities: true,
        onlyVisible: true,
    });

    const unit = data?.courses?.[0]?.units?.[0];
    const { activities } = unit ?? {};
    const imageUrl = useFileEntry({ file: unit?.imageFileEntry as FileEntry });

    const getSvg = useCallback(
        (act: Activity) => {
            if (act?.item?.type === 'quiz') {
                return isActivityCompleted(act) ? (
                    <QuizCompletedSVG className="w-6 h-6 stroke-lime-600" />
                ) : (
                    <QuizActivitySVG className="stroke-current group-hover:stroke-teal-600" />
                );
            }
            return isActivityCompleted(act) ? (
                <AssignmentCompletedSVG className="w-5 h-5 stroke-lime-600" />
            ) : (
                <ActivityIconSVG className="stroke-current group-hover:stroke-teal-600" />
            );
        },
        [activities],
    );

    return (
        <UnitActivitiesMenuList
            getSvg={getSvg}
            imageUrl={imageUrl}
            iconColor={course?.iconColor ?? '#01AAB9'}
            title={unit?.title}
            isLoading={isLoading}
            activities={activities}
            unitId={unitId}
        />
    );
}
